import { z } from 'zod';
import { assertNever } from '../requirements/index.js';
/**
 * The person lifecycle. States and legal transitions are DATA with an exhaustive guard,
 * so "can this person move there?" has exactly one answer and every caller gets it.
 *
 * Field staff never get accounts (ADR-004), so nothing here is an identity state — these
 * describe the agency's relationship with a person, not a login.
 */
export const lifecycleStateSchema = z.enum([
    /** In the pipeline; not hired. */
    'candidate',
    /** Offer accepted; working through onboarding requirements. */
    'onboarding',
    /** Employed and assignable. */
    'active',
    /** Employed but not currently assignable — leave, suspension. */
    'inactive',
    /** No longer employed. Records are retained (>= 5 yrs post-separation, HR 4.23.1). */
    'separated',
    /** Left the pipeline without being hired. */
    'withdrawn',
]);
/**
 * Legal transitions, as a total function over the state.
 *
 * The action that enters `active` also requires current derived readiness, the first day,
 * and explicit final HR clearance. This table alone never authorizes activation.
 *
 * `separated -> candidate` is rehire, which really does happen and must not require a
 * second person record — the compliance history is the point.
 */
export function allowedTransitions(from) {
    switch (from) {
        case 'candidate':
            return ['onboarding', 'withdrawn'];
        case 'onboarding':
            return ['active', 'withdrawn'];
        case 'active':
            return ['inactive', 'separated'];
        case 'inactive':
            return ['active', 'separated'];
        case 'separated':
            return ['candidate'];
        case 'withdrawn':
            return ['candidate'];
        default:
            return assertNever(from, 'allowedTransitions');
    }
}
export function canTransition(from, to) {
    return allowedTransitions(from).includes(to);
}
/**
 * THE STATUS CHANGES OF SOMEONE ALREADY EMPLOYED: on leave, back from leave, and leaving
 * (ADR-016 Amendment 2, #810). Written out rather than read from `allowedTransitions`, so a move
 * added to the table later is not a staff move until someone puts it here.
 *
 * These four are the only moves a server built without Hiring makes (`apps/api/src/auth/sections.ts`),
 * and the only ones the employee profile offers. Every candidate and hiring move, rehire included, is
 * outside this list.
 */
export const STAFF_MOVES = [
    ['active', 'inactive'],
    ['inactive', 'active'],
    ['active', 'separated'],
    ['inactive', 'separated'],
];
export function isStaffMove(from, to) {
    return STAFF_MOVES.some(([f, t]) => f === from && t === to);
}
/** The staff moves open from this state, in the order of `STAFF_MOVES`. */
export function staffMovesFrom(from) {
    return STAFF_MOVES.filter(([f]) => f === from).map(([, t]) => t);
}
/** Shared by the staff action and API; the API always re-derives readiness. */
export function activationIssues(hireDate, readiness, confirmed) {
    return [
        ...(hireDate === null ? ['Record the first day before activation.'] : []),
        ...(readiness !== 'ready' ? ['Every first-assignment requirement must be ready before activation.'] : []),
        ...(!confirmed ? ['Confirm final HR clearance before activation.'] : []),
    ];
}
/** Stable across retries and rehires, with no person information in the identifier. */
export function employeeIdFor(personId) {
    return `EMP-${personId.replaceAll('-', '').toUpperCase()}`;
}
/** True while the person is on the candidate side of the house. */
export function isCandidate(state) {
    return state === 'candidate' || state === 'onboarding';
}
/** True while the agency employs them — what makes continuous requirements live. */
export function isEmployed(state) {
    return state === 'active' || state === 'inactive';
}
//# sourceMappingURL=lifecycle.js.map